import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useRetryCounter from '../../../hooks/shared/useRetryCounter';
import useFbMsnUrl from '../../shared/useFbMsnUrl';
import { isWhatsAppMsPlatform } from '../../../../services/inbox/inboxMsChatPlatform';

/**
 * Hook de capa de datos para DocumentMC.
 * Encapsula la obtención de la URL del documento, el estado de carga y error, y la lógica de reintentos.
 * No contiene lógica de UI ni de negocio.
 */
export default function useDocumentMessageData({ data, platform, urlField = 'contentMediaUrl', maxAttempts = 3 }) {
  const [url, setUrl] = useState(data?.[urlField]);
  const [loading, setLoading] = useState(isWhatsAppMsPlatform(platform) ? false : true);
  const [error, setError] = useState(false);

  const { retryCounter, increment } = useRetryCounter({ maxAttempts });
  const getFbMsnUrl = useFbMsnUrl();

  // Intenta hacer fetch a una url, si falla llama a onError
  const tryFetchUrl = useCallback(async (targetUrl) => {
    if (!targetUrl || targetUrl === window.location.href) {
      throw new Error('URL inválida');
    }
    const resp = await fetch(targetUrl);
    if (resp.ok) {
      return targetUrl;
    } else {
      throw new Error('Fetch error');
    }
  }, []);

  useEffect(() => {
    if (isWhatsAppMsPlatform(platform)) {
      setUrl(data?.[urlField]);
      setLoading(false);
      setError(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // Solo resetea error si es un nuevo intento
    if (retryCounter === 0) {
      setError(false);
    }

    const handleError = () => {
      if (!cancelled) {
        if (retryCounter < maxAttempts - 1) {
          increment();
        } else {
          setError(true);
          setLoading(false);
        }
      }
    };

    const fetchData = async () => {
      try {
        let resultUrl;
        if (retryCounter > 0) {
          resultUrl = await tryFetchUrl(url);
        } else {
          resultUrl = await getFbMsnUrl(data);
        }
        if (!cancelled) {
          setUrl(resultUrl);
          setLoading(false);
        }
      } catch (e) {
        handleError();
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [data, platform, retryCounter]);

  return useMemo(() => ({
    url,
    loading,
    error
  }), [url, loading, error]);
}
